import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../AuthContext';
import { useEmulation } from '../../../../contexts/EmulationContext';
import { useProfileSheet } from '../../../../contexts/ProfileSheetContext';
import {
  ApiKeyRow,
  ApiKeyScope,
  connectionRecipes,
  listApiKeys,
  mintApiKey,
  revokeApiKey,
  updateApiKeyBrands,
} from '../../../../api/apiKeysApi';
import SheetField from '../shared/SheetField';
import SheetMessage from '../shared/SheetMessage';
import SectionTitle from '../shared/SectionTitle';

interface BrandOption {
  id: number;
  slug: string;
  name: string;
}

const EXPIRY_CHOICES = [30, 90, 365] as const;
const DEFAULT_EXPIRY_DAYS = 90;

const DAY_MS = 24 * 60 * 60 * 1000;

/** "expires in N days" / "expires today" / "expired" / "never expires". */
export const expiryLabel = (expiresAt: string | null, now = Date.now()): string => {
  if (!expiresAt) return 'never expires';
  const diff = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(diff)) return '';
  if (diff <= 0) return 'expired';
  const days = Math.ceil(diff / DAY_MS);
  if (days <= 1) return 'expires today';
  return `expires in ${days} days`;
};

const shortDate = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

/** A pill toggle on the sheet's chip recipe (role=checkbox for a11y). */
const Chip: React.FC<{
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ on, onToggle, disabled, children }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    className={`tend-chip${on ? ' is-active' : ''}`}
    onClick={onToggle}
    disabled={disabled}
  >
    {children}
  </button>
);

const CopyButton: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="tend-btn tend-btn--glass tend-btn--sm"
      onClick={async () => {
        const ok = await copyText(text);
        setCopied(ok);
        setTimeout(() => setCopied(false), 2000);
      }}
      aria-label={label}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/**
 * Self-serve API keys for the TEND MCP server: list, mint (token shown
 * once with the connection recipes), edit brands, revoke. While a
 * SuperAdmin emulates a user, everything here is that user's keys.
 */
const ApiKeysView: React.FC = () => {
  const { user } = useAuth();
  const { isEmulating, emulatedUser } = useEmulation();
  const { setView } = useProfileSheet();

  const onBehalf = !!(isEmulating && emulatedUser && user && emulatedUser.id !== user.id);
  const targetName = onBehalf
    ? emulatedUser?.full_name || emulatedUser?.username || emulatedUser?.email || 'this user'
    : null;
  const targetUserId = onBehalf ? emulatedUser?.id : undefined;
  // Universal-access users pick brands; everyone else has their brands
  // pre-selected (the key can never reach further than the person does).
  const targetIsUniversal = onBehalf ? !!emulatedUser?.is_super_admin : !!user?.is_super_admin;
  // queue is a SuperAdmin grant (queueing on an auto-publish line is
  // publishing); CS users share the broad admin flag but cannot grant it.
  const canGrantQueue = !!user?.is_true_super_admin;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const brandOptions: BrandOption[] = useMemo(() => {
    const source = onBehalf && emulatedUser?.brands?.length ? emulatedUser.brands : user?.brands ?? [];
    // Brand ids travel as strings in the app and as integers on the key.
    return source
      .map((b) => ({ id: Number(b?.id), slug: b?.slug ?? '', name: b?.name || b?.slug || `brand ${b?.id}` }))
      .filter((b) => Number.isInteger(b.id));
  }, [onBehalf, emulatedUser, user]);
  const brandName = useCallback(
    (id: number) => brandOptions.find((b) => b.id === id)?.name ?? `brand ${id}`,
    [brandOptions]
  );

  // ---- list ---------------------------------------------------------------
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      setKeys(await listApiKeys(showRevoked, targetUserId));
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Could not load API keys');
    } finally {
      setLoading(false);
    }
  }, [showRevoked, targetUserId]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- mint form ----------------------------------------------------------
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scopes, setScopes] = useState<Set<ApiKeyScope>>(new Set(['read', 'write']));
  const [expiresDays, setExpiresDays] = useState<number>(DEFAULT_EXPIRY_DAYS);
  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  const [minted, setMinted] = useState<{ key: ApiKeyRow; token: string } | null>(null);

  const openForm = () => {
    setName('');
    setSelected(new Set(targetIsUniversal ? [] : brandOptions.map((b) => b.id)));
    setScopes(new Set(['read', 'write']));
    setExpiresDays(DEFAULT_EXPIRY_DAYS);
    setMintError(null);
    setMinted(null);
    setFormOpen(true);
  };

  const toggleIn = <T,>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  const handleMint = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setMintError('Give the key a name you will recognise in this list');
      return;
    }
    if (selected.size === 0) {
      setMintError('Pick at least one brand');
      return;
    }
    setMinting(true);
    setMintError(null);
    try {
      const result = await mintApiKey({
        name: trimmed,
        brand_ids: Array.from(selected).sort((a, b) => a - b),
        scopes: Array.from(scopes),
        expires_days: expiresDays,
        ...(targetUserId ? { user_id: targetUserId } : {}),
      });
      setMinted(result);
      setFormOpen(false);
      setKeys((prev) => [result.key, ...prev]);
    } catch (err) {
      setMintError(err instanceof Error ? err.message : 'Could not create the key');
    } finally {
      setMinting(false);
    }
  };

  // ---- per-key actions ----------------------------------------------------
  const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<{ id: number; text: string } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBrands, setEditBrands] = useState<Set<number>>(new Set());

  const replaceRow = (row: ApiKeyRow) =>
    setKeys((rows) => rows.map((k) => (k.id === row.id ? row : k)));

  const handleRevoke = async (id: number) => {
    setBusyId(id);
    setRowError(null);
    try {
      replaceRow(await revokeApiKey(id));
      setConfirmRevokeId(null);
    } catch (err) {
      setRowError({ id, text: err instanceof Error ? err.message : 'Could not revoke the key' });
    } finally {
      setBusyId(null);
    }
  };

  const startEditBrands = (row: ApiKeyRow) => {
    setEditingId(row.id);
    setEditBrands(new Set(row.brand_ids));
    setRowError(null);
  };

  const saveBrands = async (id: number) => {
    if (editBrands.size === 0) {
      setRowError({ id, text: 'A key needs at least one brand' });
      return;
    }
    setBusyId(id);
    setRowError(null);
    try {
      replaceRow(await updateApiKeyBrands(id, Array.from(editBrands).sort((a, b) => a - b)));
      setEditingId(null);
    } catch (err) {
      setRowError({ id, text: err instanceof Error ? err.message : 'Could not update the key' });
    } finally {
      setBusyId(null);
    }
  };

  const recipes = minted ? connectionRecipes(origin, minted.token) : null;

  if (!user) return null;

  return (
    <div className="tend-stack tend-stack--loose">
      <div>
        <div className="tend-micro">Keys for {hostname}</div>
        <p className="tend-sheet-lead">
          {onBehalf
            ? `${targetName}’s keys. A key reaches only brands they already can.`
            : 'A key lets Claude Desktop, claude.ai or Claude Code read this account’s brands and land finished work in Tended. Keys are per person and per environment.'}
        </p>
      </div>

      {minted && recipes && (
        <div className="tend-note tend-note--ok tend-recipe">
          <div className="tend-recipe-row">
            <span className="tend-recipe-title">{minted.key.name} is ready. This token is shown once.</span>
            <CopyButton text={minted.token} label="Copy token" />
          </div>
          <pre className="tend-mono" data-testid="minted-token">{minted.token}</pre>

          <div className="tend-micro">Claude Code</div>
          <div className="tend-recipe-row">
            <pre className="tend-mono">{recipes.claudeCode}</pre>
            <CopyButton text={recipes.claudeCode} label="Copy Claude Code command" />
          </div>

          <div className="tend-micro">Claude Desktop / claude.ai</div>
          <div className="tend-recipe-tip">
            Settings &rarr; Connectors &rarr; Add custom connector &rarr; URL{' '}
            <code className="tend-mono">{recipes.mcpUrl}</code>
            {' '}&rarr; Authentication: <b>No sign in</b> &rarr; Request header
          </div>
          <div className="tend-recipe-row">
            <pre className="tend-mono">{recipes.header}</pre>
            <CopyButton text={recipes.header} label="Copy header" />
          </div>

          <div className="tend-recipe-tip">
            Tool permissions: keep reads on always allow and writes on needs approval. The token will not
            be shown again &mdash; revoke and mint a new one if it is lost.
          </div>
          <div className="tend-links-row">
            <button type="button" className="tend-link" onClick={() => setMinted(null)}>
              Done
            </button>
          </div>
        </div>
      )}

      {!formOpen && !minted && (
        <button
          type="button"
          className="tend-btn tend-btn--cta tend-btn--block"
          onClick={openForm}
          disabled={brandOptions.length === 0}
        >
          New key
        </button>
      )}
      {brandOptions.length === 0 && (
        <SheetMessage kind="info">No brand to scope a key to yet.</SheetMessage>
      )}

      {formOpen && (
        <form onSubmit={handleMint} className="tend-stack">
          {mintError && <SheetMessage kind="err">{mintError}</SheetMessage>}
          <SheetField
            label="Key name (e.g. desktop · laptop)"
            value={name}
            onChange={setName}
            name="api-key-name"
            disabled={minting}
            autoFocus
          />

          <div className="tend-micro">Brands</div>
          <div className="tend-chips">
            {brandOptions.map((b) => (
              <Chip
                key={b.id}
                on={selected.has(b.id)}
                onToggle={() => setSelected((s) => toggleIn(s, b.id))}
                disabled={minting}
              >
                {b.name}
              </Chip>
            ))}
          </div>

          <div className="tend-micro">Scopes</div>
          <div className="tend-chips">
            <Chip on={scopes.has('read')} onToggle={() => setScopes((s) => toggleIn(s, 'read'))} disabled={minting}>
              read
            </Chip>
            <Chip on={scopes.has('write')} onToggle={() => setScopes((s) => toggleIn(s, 'write'))} disabled={minting}>
              write
            </Chip>
            {canGrantQueue && (
              <Chip on={scopes.has('queue')} onToggle={() => setScopes((s) => toggleIn(s, 'queue'))} disabled={minting}>
                queue
              </Chip>
            )}
          </div>

          <div className="tend-micro">Expires</div>
          <div className="tend-chips">
            {EXPIRY_CHOICES.map((d) => (
              <Chip key={d} on={expiresDays === d} onToggle={() => setExpiresDays(d)} disabled={minting}>
                {d === 365 ? '1 year' : `${d} days`}
              </Chip>
            ))}
          </div>

          <button type="submit" className="tend-btn tend-btn--cta tend-btn--block" disabled={minting}>
            {minting ? 'Creating…' : 'Create key'}
          </button>
          <div className="tend-links-row" style={{ marginTop: 0 }}>
            <button type="button" className="tend-link" onClick={() => setFormOpen(false)} disabled={minting}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <SectionTitle
        meta={
          <button
            type="button"
            className="tend-link tend-micro"
            onClick={() => setShowRevoked((v) => !v)}
          >
            {showRevoked ? 'Hide revoked' : 'Show revoked'}
          </button>
        }
      >
        {onBehalf ? `${targetName}’s keys` : 'Your keys'}
      </SectionTitle>

      {loading && (
        <div className="tend-progress">
          <i style={{ width: '40%' }} />
        </div>
      )}
      {listError && <SheetMessage kind="err">{listError}</SheetMessage>}
      {!loading && !listError && keys.length === 0 && <div className="tend-empty">No keys yet.</div>}

      <div className="tend-stack" style={{ gap: 8 }}>
        {keys.map((row) => {
          const revoked = !!row.revoked_at;
          const expired = !revoked && !!row.expires_at && new Date(row.expires_at).getTime() <= Date.now();
          const busy = busyId === row.id;
          return (
            <div
              key={row.id}
              className={`tend-section-card tend-key${revoked ? ' is-revoked' : ''}`}
              data-testid={`api-key-${row.id}`}
            >
              <div className="tend-key-head">
                <span className="tend-key-name">{row.name}</span>
                <span className="tend-micro">{row.key_prefix}&hellip;</span>
              </div>
              <div className="tend-key-line">
                {row.brand_ids.map(brandName).join(', ')}
                {' · '}
                {row.scopes.join(' + ')}
              </div>
              <div className="tend-key-sub">
                {revoked ? (
                  <span>Revoked {shortDate(row.revoked_at)}</span>
                ) : (
                  <span>{expired ? 'expired' : expiryLabel(row.expires_at)}</span>
                )}
                {' · created '}
                {shortDate(row.created_at)}
                {' · '}
                {row.last_used_at ? `last used ${shortDate(row.last_used_at)}` : 'never used'}
              </div>

              {rowError?.id === row.id && <SheetMessage kind="err">{rowError.text}</SheetMessage>}

              {!revoked && editingId === row.id && (
                <div className="tend-key-edit">
                  <div className="tend-chips">
                    {brandOptions.map((b) => (
                      <Chip
                        key={b.id}
                        on={editBrands.has(b.id)}
                        onToggle={() => setEditBrands((s) => toggleIn(s, b.id))}
                        disabled={busy}
                      >
                        {b.name}
                      </Chip>
                    ))}
                  </div>
                  <div className="tend-key-edit-actions">
                    <button
                      type="button"
                      className="tend-btn tend-btn--glass tend-btn--sm"
                      onClick={() => saveBrands(row.id)}
                      disabled={busy}
                    >
                      {busy ? 'Saving…' : 'Save brands'}
                    </button>
                    <button type="button" className="tend-link" onClick={() => setEditingId(null)} disabled={busy}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {!revoked && confirmRevokeId === row.id && (
                <div className="tend-key-confirm">
                  <span>Revoke this key? Anything connected with it stops working now.</span>
                  <button
                    type="button"
                    className="tend-btn tend-btn--glass tend-btn--sm tend-btn--danger"
                    onClick={() => handleRevoke(row.id)}
                    disabled={busy}
                    aria-label={`Confirm revoke ${row.name}`}
                  >
                    {busy ? 'Revoking…' : 'Revoke'}
                  </button>
                  <button type="button" className="tend-link" onClick={() => setConfirmRevokeId(null)} disabled={busy}>
                    Keep
                  </button>
                </div>
              )}

              {!revoked && editingId !== row.id && confirmRevokeId !== row.id && (
                <div className="tend-links-row tend-key-actions">
                  <button type="button" className="tend-link" onClick={() => startEditBrands(row)}>
                    Edit brands
                  </button>
                  <button
                    type="button"
                    className="tend-link"
                    onClick={() => setConfirmRevokeId(row.id)}
                    aria-label={`Revoke ${row.name}`}
                  >
                    Revoke
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="tend-links-row">
        <button type="button" className="tend-link" onClick={() => setView('profile')}>
          Back to profile
        </button>
      </div>
    </div>
  );
};

export default ApiKeysView;
